
import {
  integrationConfig,
  isBackendConfigured,
} from './integrationConfig'

import type {
  MembershipPlanId,
  PractitionerAccountInput,
  PractitionerApiErrorCode,
  PractitionerSession,
} from '../types/practitionerSignup'

/**
 * Browser → Supabase integration for the web practitioner signup flow.
 *
 * Architecture:
 *
 *   Website
 *      └──► Edge Function `create-practitioner-account`
 *              └──► Supabase Auth
 *
 *   Website
 *      └──► Supabase Auth password grant
 *              └──► practitioner session JWT
 *
 *   Website
 *      └──► Edge Function `create-practitioner-subscription`
 *              └──► Stripe subscription
 *              └──► Stripe SetupIntent
 *
 * The subscription Edge Function returns the Stripe client secrets
 * needed by the frontend to securely collect/save the practitioner's
 * payment method.
 *
 * SECURITY
 * - Passwords are sent over HTTPS and are never logged.
 * - Never put Supabase service_role/secret keys or Stripe secret keys
 *   in frontend code.
 * - The subscription Edge Function must derive/verify the user ID
 *   from the authenticated JWT.
 */

export class PractitionerApiError extends Error {
  code: PractitionerApiErrorCode

  constructor(code: PractitionerApiErrorCode, message: string) {
    super(message)
    this.name = 'PractitionerApiError'
    this.code = code
  }
}

function assertConfigured() {
  if (!isBackendConfigured()) {
    throw new PractitionerApiError(
      'not_configured',
      'Practitioner signup is not connected yet.',
    )
  }
}

function publicHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    apikey: integrationConfig.supabaseAnonKey,
  }
}

async function readJson(
  response: Response,
): Promise<Record<string, unknown>> {
  try {
    return (await response.json()) as Record<string, unknown>
  } catch {
    return {}
  }
}

async function request(
  url: string,
  init: RequestInit,
): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch {
    throw new PractitionerApiError(
      'network',
      'We couldn’t reach Fem Entity. Check your connection and try again.',
    )
  }
}

function serverMessage(
  body: Record<string, unknown>,
): string | undefined {
  const candidates = [
    body.error,
    body.message,
    body.msg,
    body.error_description,
  ]

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate
    }

    if (candidate && typeof candidate === 'object') {
      const nested = (candidate as Record<string, unknown>).message

      if (typeof nested === 'string' && nested.trim()) {
        return nested
      }
    }
  }

  return undefined
}

/**
 * True when the Supabase response indicates that the email already exists.
 */
function isDuplicateEmail(
  body: Record<string, unknown>,
): boolean {
  const code =
    typeof body.code === 'string'
      ? body.code
      : typeof body.error_code === 'string'
        ? body.error_code
        : ''

  if (
    code === 'email_exists' ||
    code === 'user_already_exists'
  ) {
    return true
  }

  const text = (serverMessage(body) ?? '').toLowerCase()

  return (
    text.includes('already') &&
    (text.includes('registered') || text.includes('exist'))
  )
}

/**
 * Headers for calling an Edge Function without a user session.
 *
 * New-format publishable keys (`sb_publishable_...`) are not JWTs,
 * so they are sent in the `apikey` header only.
 *
 * Legacy anon JWT keys are also sent as the Bearer token.
 */
function anonymousFunctionHeaders(): Record<string, string> {
  const headers = publicHeaders()

  if (!integrationConfig.supabaseAnonKey.startsWith('sb_')) {
    headers.Authorization =
      `Bearer ${integrationConfig.supabaseAnonKey}`
  }

  return headers
}

/**
 * Create the practitioner account through the Supabase Edge Function.
 */
export async function createPractitionerAccount(
  input: PractitionerAccountInput,
): Promise<void> {
  // Temporary diagnostic.
  // This does NOT expose the actual anon key.
  console.log('SUPABASE CONFIG CHECK', {
    supabaseUrl: integrationConfig.supabaseUrl,
    hasAnonKey: Boolean(integrationConfig.supabaseAnonKey),
    createAccountFunction:
      integrationConfig.createAccountFunction,
    backendConfigured: isBackendConfigured(),
  })

  assertConfigured()

  const response = await request(
    `${integrationConfig.supabaseUrl}/functions/v1/${integrationConfig.createAccountFunction}`,
    {
      method: 'POST',
      headers: anonymousFunctionHeaders(),
      body: JSON.stringify(input),
    },
  )

  const body = await readJson(response)

  const reportedError =
    body.success === false ||
    (body.error !== undefined && body.error !== null)

  if (response.ok && !reportedError) {
    return
  }

  if (
    response.status === 409 ||
    isDuplicateEmail(body)
  ) {
    throw new PractitionerApiError(
      'email_exists',
      'An account with this email already exists.',
    )
  }

  throw new PractitionerApiError(
    'server',
    serverMessage(body) ??
      'We couldn’t create your account. Please try again.',
  )
}

/**
 * Sign in the practitioner through Supabase Auth.
 */
export async function signInPractitioner(
  email: string,
  password: string,
): Promise<PractitionerSession> {
  assertConfigured()

  const response = await request(
    `${integrationConfig.supabaseUrl}/auth/v1/token?grant_type=password`,
    {
      method: 'POST',
      headers: publicHeaders(),
      body: JSON.stringify({
        email,
        password,
      }),
    },
  )

  const body = await readJson(response)

  if (!response.ok) {
    const code =
      typeof body.error_code === 'string'
        ? body.error_code
        : ''

    if (code === 'email_not_confirmed') {
      throw new PractitionerApiError(
        'email_not_confirmed',
        'Please confirm your email address, then sign in to continue.',
      )
    }

    if (
      response.status === 400 ||
      code === 'invalid_credentials'
    ) {
      throw new PractitionerApiError(
        'invalid_credentials',
        'That email and password don’t match our records.',
      )
    }

    throw new PractitionerApiError(
      'server',
      serverMessage(body) ??
        'We couldn’t sign you in. Please try again.',
    )
  }

  const user = (body.user ?? {}) as {
    id?: string
    email?: string
  }

  const accessToken =
    typeof body.access_token === 'string'
      ? body.access_token
      : ''

  const expiresIn =
    typeof body.expires_in === 'number'
      ? body.expires_in
      : 3600

  if (!accessToken || !user.id) {
    throw new PractitionerApiError(
      'server',
      'We couldn’t start your session. Please try again.',
    )
  }

  return {
    accessToken,
    userId: user.id,
    email: user.email ?? email,
    expiresAt: Date.now() + expiresIn * 1000,
  }
}

/**
 * Response returned by the `create-practitioner-subscription`
 * Supabase Edge Function.
 *
 * The Edge Function creates the Stripe subscription and returns
 * the information needed by the frontend to complete payment-method
 * setup and display membership status.
 */
export interface PractitionerSubscriptionResponse {
  success: boolean
  existingSubscription: boolean
  clientSecret?: string
  setupIntentClientSecret?: string
  subscriptionId: string
  customerId: string
  status: string
  tierName: MembershipPlanId
  planType: 'monthly' | 'annual'
  amountBilled: number
  trialEndDate: string
}

/**
 * Create the practitioner's Stripe subscription through the
 * Supabase Edge Function.
 *
 * This replaces the previous Stripe Checkout flow.
 *
 * The current founding membership flow is monthly only.
 *
 * The Edge Function:
 * - validates the authenticated practitioner
 * - validates the selected tier
 * - creates/reuses the Stripe customer
 * - creates the Stripe subscription
 * - applies the two-month trial
 * - creates/retrieves the Stripe SetupIntent
 * - saves the practitioner subscription in Supabase
 *
 * The frontend receives the SetupIntent client secret and can then
 * use Stripe.js to securely collect/save the payment method.
 */
export async function startPractitionerSubscription(
  planId: MembershipPlanId,
  session: PractitionerSession,
): Promise<PractitionerSubscriptionResponse> {
  assertConfigured()

  const response = await request(
    `${integrationConfig.supabaseUrl}/functions/v1/${integrationConfig.checkoutFunction}`,
    {
      method: 'POST',
      headers: {
        ...publicHeaders(),
        Authorization: `Bearer ${session.accessToken}`,
      },
      body: JSON.stringify({
        userId: session.userId,
        tierName: planId,
        planType: 'monthly',
      }),
    },
  )

  const body = await readJson(response)

  if (!response.ok) {
    throw new PractitionerApiError(
      'server',
      serverMessage(body) ??
        'We couldn’t start your membership. Please try again.',
    )
  }

  /**
   * Validate the important fields returned by the Edge Function.
   *
   * We intentionally do not require either clientSecret or
   * setupIntentClientSecret here because Stripe can return different
   * combinations depending on the subscription/trial state.
   */
  if (
    body.success !== true ||
    typeof body.subscriptionId !== 'string' ||
    typeof body.customerId !== 'string' ||
    typeof body.status !== 'string' ||
    typeof body.tierName !== 'string' ||
    typeof body.planType !== 'string' ||
    typeof body.amountBilled !== 'number' ||
    typeof body.trialEndDate !== 'string'
  ) {
    throw new PractitionerApiError(
      'server',
      'We received an incomplete membership response. Please try again.',
    )
  }

  /**
   * Make sure the backend returned a tier we actually support.
   */
  const validPlanIds: MembershipPlanId[] = [
    'anchor',
    'bloom',
    'thrive',
    'collective',
  ]

  if (!validPlanIds.includes(body.tierName as MembershipPlanId)) {
    throw new PractitionerApiError(
      'server',
      'The selected membership plan is not valid. Please choose another plan.',
    )
  }

  /**
   * The current website supports monthly memberships.
   * Keep the response validation explicit so an unexpected backend
   * response does not silently create the wrong membership type.
   */
  if (
    body.planType !== 'monthly' &&
    body.planType !== 'annual'
  ) {
    throw new PractitionerApiError(
      'server',
      'The membership billing period is not valid. Please try again.',
    )
  }

  return {
    success: true,
    existingSubscription:
      body.existingSubscription === true,

    clientSecret:
      typeof body.clientSecret === 'string'
        ? body.clientSecret
        : undefined,

    setupIntentClientSecret:
      typeof body.setupIntentClientSecret === 'string'
        ? body.setupIntentClientSecret
        : undefined,

    subscriptionId: body.subscriptionId,
    customerId: body.customerId,
    status: body.status,
    tierName: body.tierName as MembershipPlanId,
    planType: body.planType as 'monthly' | 'annual',
    amountBilled: body.amountBilled,
    trialEndDate: body.trialEndDate,
  }
}

/**
 * Convert API/runtime errors into a user-friendly message.
 *
 * Uses structural checking instead of `instanceof` because the
 * Magic Patterns runtime can have module-boundary issues with
 * class-based instanceof checks.
 */
export function describeError(error: unknown): string {
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message?: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message
  }

  return 'Something went wrong. Please try again.'
}